import { FC } from 'react';
import cn from 'clsx';

import { makeStyles } from '@material-ui/styles';
import { UserInfoHeader, MainButton, Pencil } from '@bizzabo/design-system';

import { useCurrentUserContext } from '../../context/CurrentUserProvider';
import { useUserProfileViewContext, ViewType } from '../../context/UserProfileViewProvider';
import { useCountryByCountryCode } from '../../helpers/countries';
import { useCountryState } from '../../helpers/states';
import { useProfileAnalytics, useTruncateSummary } from '../../hooks';
import HideProfileToggle from '../HideProfileToggle';
import ReadViewItem from '../ReadViewItem';

const ReadView: FC = () => {
    const { user } = useCurrentUserContext();
    const { modalView, setViewType } = useUserProfileViewContext();

    const classes = useStyles();

    const country = useCountryByCountryCode(user.country)?.label || '';
    const countryState = useCountryState(user.country, user.state)?.label || '';
    const summary = useTruncateSummary(user?.summary);

    const { sendEnterEditMode } = useProfileAnalytics();

    const handleEdit = () => {
        setViewType(ViewType.EDIT);
        sendEnterEditMode();
    };

    return (
        <div className={cn({ [classes.container]: modalView })}>
            <div className={classes.header}>
                <UserInfoHeader
                    user={user}
                    userIconSize="XX-Large"
                    titleVariant="t71"
                    descriptionVariant="t81"
                />
                <MainButton
                    className={cn(classes.editButton, {
                        [classes.editButtonModalView]: modalView,
                    })}
                    before={<Pencil width="14" height="14" />}
                    onClick={handleEdit}
                    size={modalView ? 'large' : 'small'}
                >
                    Edit
                </MainButton>
            </div>
            <div className={cn(classes.main, { [classes.modalMain]: modalView })}>
                <ReadViewItem label="First name" placeholder="First name" value={user.firstName} />
                <ReadViewItem label="Last name" placeholder="Last name" value={user.lastName} />
                <ReadViewItem
                    label="LinkedIn profile"
                    placeholder="https://www.linkedin.com/in/yourprofile"
                    value={user.linkedinId}
                />
                <ReadViewItem
                    label="Twitter handle"
                    placeholder="@yourhandle"
                    value={user.twitterHandle}
                />

                <ReadViewItem label="Company" placeholder="E.g. Google" value={user.company} />
                <ReadViewItem
                    label="Company website"
                    placeholder="www.yourcompany.com"
                    value={user.web}
                />
                <div className={classes.section}>
                    <ReadViewItem
                        label="Role"
                        placeholder="E.g. Account Manager"
                        value={user.title}
                    />
                </div>
                <div className={classes.section}>
                    <ReadViewItem
                        label="About me"
                        placeholder="Introduce yourself to help others connect with you"
                        clamp="none"
                        value={summary}
                    />
                </div>
                <ReadViewItem label="Country" placeholder="Country name" value={country} />
                <ReadViewItem label="State" placeholder="State name" value={countryState} />

                <div className={classes.footer}>
                    <HideProfileToggle
                        eventId={user.eventId}
                        userProfileId={user.userProfileId}
                        isHidden={user.isHidden}
                    />
                </div>
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    () => ({
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: '0 30px 30px',
        },
        main: {
            display: 'block',
            paddingBottom: 10,

            '& > *': {
                marginBottom: 14,
            },
        },
        modalMain: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, calc(50% - 4px))',
            gridColumnGap: 8,
            gridRowGap: 12,
            overflowY: 'auto',

            '& > *': {
                marginBottom: 0,
            },
        },
        editButton: {
            padding: '2px 12px',
        },
        editButtonModalView: {
            border: 'none',
            padding: '.75em 1em',
        },
        header: {
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: 16,
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            marginTop: 12,
        },
        section: {
            gridColumn: '1 / 3',
        },
    }),
    { classNamePrefix: 'people-user-profile-read-view' }
);

export default ReadView;
